/**
 * Email Creative Hub — React rebuild matching the production monolith shell.
 * Renamed from "Email Creative Studio" (Jack, 2026-08-19). The TOOL is the Hub;
 * the drag-and-drop block editor inside it is still called the Creative Studio,
 * and so is the Airtable base ("Creative Studio Projects") — those names stay.
 * Single linear wizard (the Request flow IS the app); the Creative Studio
 * launches from inside the flow, and the Review Board / Template Edit tools live
 * under the ⚙ Admin menu. Topbar: Lennar logo + "EMAIL CREATIVE HUB" crumb +
 * Feedback + Admin — no top tabs (production parity).
 *
 * React owns the UI; the email markup itself comes only from the pure-TS
 * renderers in src/email/ (parity-gated byte-identical to production).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ui } from "./services/uiState";
import "./views/request-builder.css";
import { FULL_REGISTRY } from "./email/blocks";
import { renderDocument, type AnyBlockInstance } from "./email/document";
import { useEchShell } from "./services/useEchShell";
import { echSetActiveTemplate } from "./services/echSnap";
import BynderPicker from "./components/BynderPicker";
import type { BynderMultiOpen } from "./components/Inspector";
import FormView from "./views/FormView";
import StudioView from "./views/StudioView";
import { footerWithDisclaimer, isDisclaimerBlock } from "./services/disclaimerLive";
import BoardView from "./views/BoardView";
import { disclaimerId, type FormState } from "./services/submit";
import { liveDisclaimerParas } from "./services/disclaimerLive";
import { newSeg, type SegState } from "./services/segData";
import { sendFeedback } from "./services/api";
import { getCurrentUser, signOut } from "./services/auth";
import { MVP_TPL_DEFAULT } from "./services/mvpMode";
import type { CopyContext } from "./services/aiCopy";
import type { AiCopyResult } from "./components/AiCopyModal";
import { divisionOptions, messageHuman, messageIsOffer } from "./services/requestData";
import { echSeedStrip } from "./services/echDyn";
import { echAsmOf, echDoc, echStamp, echTplFor, echTplVersion } from "./services/echTemplates";
import { echVersion, isEch, rebindEchTypes } from "./services/echRegistry";
import { sfmcBlocksOnce } from "./services/sfmcSource";
import { echAdopt } from "./services/echSnap";
import { useEchTemplateList } from "./services/useEchTemplates";
import { tplStageOf } from "./services/dupJobs";
import { divMarkets } from "./services/snapshots";
import type { Notice } from "./services/manifest";
import { draftAge, draftClear, draftLoad, draftMeaningful, draftSave, draftTitle, DRAFT_DEBOUNCE_MS, type Draft } from "./services/draft";
import { namedDraftsEnabled, type NamedDraft, type NamedDraftDoc } from "./services/namedDrafts";
import { disclaimerBuilderEnabled } from "./services/features";
import { useLicenseLine } from "./services/useLicenseLine";
import { useLegalDisclaimers } from "./services/useLegalDisclaimers";
import { draftMarkSubmitted, draftSaveRemote, getNamedDraftDoc, getProof, getProofVersionDoc, mirrorRevise, proofPost, type ProofHighlight, type ProofOp, type ProofView as ProofData } from "./services/api";
import { proofReviewUrl, proofRouted } from "./views/proofRoute";

let nextId = 1;
const uid = () => "b" + nextId++;

/**
 * Initials for the topbar identity chip — first + last word, so "Jacob Levy" → JL.
 *
 * Falls back to the first two characters of a single-word name, because Entra's
 * display name is not guaranteed to be two words (a service account or a name
 * with no space would otherwise render one lonely letter). Never returns empty:
 * an empty chip reads as a rendering bug rather than as "nobody is signed in".
 */
export function initials(name: string): string {
  const w = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "?";
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[w.length - 1][0]).toUpperCase();
}
const STARTER = ["logo", "hero", "headline", "body", "cta", "footer"];
const makeBlock = (type: string): AnyBlockInstance => {
  const b: AnyBlockInstance = { id: uid(), type, data: FULL_REGISTRY[type].def() as Record<string, unknown> };
  /* a SEED field's authored default ("City, State") is the panel's grey hint, not
     content — every block enters the document blank there (see echSeedStrip) */
  echSeedStrip(b);
  return b;
};

type View = "form" | "studio" | "board";

/* ── THE ADMIN BOARD IS URL-ONLY (2026-08-19, per Jack) ────────────────────────
   The ⚙ Admin button is gone from the top bar, so divisions never see the review
   console. It stays reachable at `?admin=1` (or `#admin`) for Jack and email ops.

   Be clear about what this is: it hides the door, it does NOT lock it. The board's
   code ships in the same bundle and the param is guessable, so anyone who learns the
   URL is in. That's fine while the board only shows the creative-review queue this
   audience may see anyway — but if it ever surfaces something the divisions must not
   read, it needs a real server-side gate, not a query string. Deliberately NOT
   persisted to localStorage: a stray ?admin=1 shouldn't stick to someone's browser. */
function adminRouted(): boolean {
  try {
    if (typeof window === "undefined") return false;
    const qs = new URLSearchParams(window.location.search);
    const v = (qs.get("admin") || "").toLowerCase();
    if (v === "1" || v === "true" || v === "yes") return true;
    return /^#\/?admin$/i.test(window.location.hash || "");
  } catch { return false; }
}

export default function App() {
  const [view, setView] = useState<View>(adminRouted() ? "board" : "form");
  const [fbOpen, setFbOpen] = useState(false);
  const [fbText, setFbText] = useState("");
  const [fbCat, setFbCat] = useState("User Experience");
  const [fbBy, setFbBy] = useState("");
  const [fbImgs, setFbImgs] = useState<{ name: string; data: string }[]>([]);
  const [fbBusy, setFbBusy] = useState(false);

  /* bottom-center toast (monolith __fvToast) — the feedback contract toasts */
  const [toastMsg, setToastMsg] = useState("");
  const [toastOn, setToastOn] = useState(false);
  const toastT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toast = (m: string) => {
    setToastMsg(m); setToastOn(true);
    if (toastT.current) clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToastOn(false), 2400);
  };

  /* form + audience-segment state (Request wizard) */
  const [form, setForm] = useState<FormState>({ projectName: "", divisionCode: "", requestedByEmail: "" });
  const [seg, setSeg] = useState<SegState>(newSeg());
  /* wizard page lives HERE, not in FormView: the monolith's studio is an overlay so the
     form keeps its page across open/close — the port unmounts FormView on view switch,
     and a FormView-local step reset Done back to page 1 (found in the 2026-08-09
     pre-push live drive: monolith Done → page 2, port Done → page 1). */
  const [formStep, setFormStep] = useState(1);

  /* Studio (block build) state */
  const [blocks, setBlocks] = useState<AnyBlockInstance[]>(() => STARTER.map(makeBlock));
  /* Bynder routing (2026-08-08 inspector deep-parity): the picker no longer patches a
     fixed {blockId, k} — the opener hands over an `apply` that routes the pick (plain
     field + alt autofill, repeat-item field, film-strip append), like the monolith's
     window.__bynderPick(onPick). */
  const [bynderFor, setBynderFor] = useState<{ apply: (url: string, asset: { name?: string; alt?: string }) => void; need?: { w?: number; h?: number }; multi?: BynderMultiOpen } | null>(null);

  /* Template edit-in-place state */
  const [tplHtml, setTplHtml] = useState("");
  const [tplEdited, setTplEdited] = useState("");
  /* Is the loaded document BESPOKE? Only the STANDARD template may be assembled
     from Layne's SFMC blocks (audit row 23): a sent email ("As sent · …"), a
     division favorite ("★ …") and a community-filled build are one-off documents,
     and rebuilding one of those into a template's block sequence would throw away
     the very thing the user asked to open.
     Decided AT LOAD TIME, not by comparing names later — the user can change the
     template afterwards, and a live comparison then read every standard load as
     bespoke and stranded the Studio on the previous template's preview. */
  const [tplBespoke, setTplBespoke] = useState(false);
  /* HTML the FORM loaded programmatically (sent email / community fill) — the
     monolith's __studio.used() must not count it as the user's own build */
  const autoLoadedRef = useRef("");

  /* ── DID THE DIVISION ACTUALLY BUILD SOMETHING? (2026-08-24) ──────────────────
     `studioUsed` gates whether FormView hands the block doc to buildSubmitBody,
     and therefore whether the ENTIRE visual spec ships — every photo URL, CTA
     label, CTA url, Block Order and Build Spec (JSON). It used to be read off the
     UNDO HISTORY alone, which is wrong in the one flow that matters:

       • field edits deliberately bypass history (`patchData`, see the note at the
         undo refs below — "field edits stay live/non-history"), so filling in the
         whole Inspector leaves `pastRef` empty; and
       • assembling a template CLEARS the history on purpose
         (StudioView `if (fresh && onResetHistory) onResetHistory()`).

     So a division that picked a template and filled it in — the entire MVP flow —
     submitted with studioUsed FALSE and no visual spec at all. Measured on a
     filled Coming Soon-shaped doc: 21 fields land with the doc, 6 without it, and
     the 15 lost are exactly the columns creative rebuilds the email from. That is
     Katy's 2026-08-22 report ("no photo, button, url or copy") and it also silently
     skipped the required-fields submit gate, which is `studioUsed ? ... : []`.

     The honest signal is the DOCUMENT, not the history: once the canvas holds
     anything other than the untouched STARTER mock, a real build exists. Kept as a
     divergence test rather than "did they open the Studio" so the original guard
     survives — an untouched starter doc must still never be filed as a build. */
  const docBuilt = useMemo(
    () => blocks.length !== STARTER.length || blocks.some((b, i) => b.type !== STARTER[i]),
    [blocks],
  );

  /* THE SHELL RIDES EVERY RENDER (2026-09-15). This is the HTML the form previews and
     the submit attaches; without the shell it shipped the fallback head while the
     Studio's own export carried the SFMC template — see services/useEchShell.ts. */
  /* …and PER TEMPLATE (2026-10-06): a template that names its own shell
     (`"shell":"ECH_V2_Shell"`) renders in it. Registered before any render so the
     form rail and the submit, which render through the provider, follow it too. */
  const shellTpl = (Array.isArray(form.template) ? form.template[0] : form.template) || MVP_TPL_DEFAULT;
  echSetActiveTemplate(shellTpl);
  const shell = useEchShell(shellTpl);
  const studioHtml = useMemo(() => renderDocument(blocks, shell), [blocks, shell]);
  /** what a submit attaches: template edits win when present, else the Studio build */
  const builtHtml = tplEdited || studioHtml;
  const div = form.divisionCode;

  /* Studio undo/redo — structural ops route through studioSet (history); field
     edits (patchData) stay live/non-history. */
  const pastRef = useRef<AnyBlockInstance[][]>([]);
  const futureRef = useRef<AnyBlockInstance[][]>([]);
  const [, setHistVer] = useState(0);
  /* the live document, for `patchData` (stable identity) to snapshot — see its note */
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  /* the field-edit coalescing group (#131): block + keys, and when it last wrote */
  const histGroupRef = useRef<{ key: string; at: number }>({ key: "", at: 0 });
  const studioSet = (updater: (b: AnyBlockInstance[]) => AnyBlockInstance[]) => {
    pastRef.current.push(blocks);
    if (pastRef.current.length > 80) pastRef.current.shift();
    futureRef.current = [];
    histGroupRef.current = { key: "", at: 0 };
    setBlocks(updater);
    setHistVer((v) => v + 1);
  };
  const studioUndo = () => {
    const prev = pastRef.current.pop();
    if (!prev) return;
    futureRef.current.push(blocks);
    histGroupRef.current = { key: "", at: 0 };
    setBlocks(prev);
    setHistVer((v) => v + 1);
  };
  const studioRedo = () => {
    const next = futureRef.current.pop();
    if (!next) return;
    pastRef.current.push(blocks);
    histGroupRef.current = { key: "", at: 0 };
    setBlocks(next);
    setHistVer((v) => v + 1);
  };
  /* monolith esResetHist() — loading a template replaces the whole document, so
     the pre-load doc must not be one ⌘Z away (L9204 calls it from loadTpl) */
  const studioResetHist = () => {
    pastRef.current = []; futureRef.current = [];
    histGroupRef.current = { key: "", at: 0 };
    setHistVer((v) => v + 1);
  };

  /**
   * A DUPLICATE ARRIVES AS BLOCKS, NOT AS A STRING (2026-08-20).
   *
   * `tplHtml` MUST be cleared here. StudioView decides between the block canvas and
   * the flat template editor with `editing = !!tplHtml && …` — so leaving a previous
   * document in place would hand the canvas to `TemplateEditor` and the rebuilt
   * blocks would be invisible, which is exactly the "it exists but nothing routes to
   * it" failure the reachability gate exists to catch.
   *
   * History is reset for the same reason `loadTpl` resets it: the document was
   * replaced wholesale, and the pre-duplicate document must not be one ⌘Z away.
   *
   * AND `tplBespoke` MUST BE TRUE. A duplicate is a one-off document, exactly like
   * "As sent · …" and "★ …" are — `echTplName` is `tplBespoke ? "" : tplName`, so
   * leaving it false lets `echDoc()` assemble the STANDARD template over the top.
   * Measured: the first version of this set it false, and the duplicate's 2 blocks
   * were replaced on screen by the 12-block Coming Soon assembly — the rebuild was
   * correct and instantly discarded. Caught only because App.duplicate.test.tsx
   * drives the real path; every service-level test passed throughout.
   */
  const [dupNotices, setDupNotices] = useState<Notice[]>([]);
  /**
   * The Studio holds a BLOCK document the user asked for.
   *
   * Needed because `studioHasDoc` — which is what `FormView.openStudio` checks before
   * loading the selected template — was `!!tplHtml`, i.e. it could only see a FLAT
   * document. A duplicate clears `tplHtml` by design, so the opener read "nothing
   * loaded", called `onLoadStudioHtml(tplName, previewHtml)`, and that reset
   * `tplBespoke` to false and let `echDoc()` assemble the standard template straight
   * over the duplicate. Measured: 12 Coming Soon blocks on the canvas where the
   * duplicate's 2 should have been. Fixing `tplBespoke` alone was not enough — the
   * flat-document assumption is the actual fault, and this is the flag that removes it.
   */
  const [dupLoaded, setDupLoaded] = useState(false);
  const loadStudioBlocks = useCallback((_name: string, next: AnyBlockInstance[], notices: Notice[]) => {
    autoLoadedRef.current = "";
    setTplHtml(""); setTplEdited(""); setTplBespoke(true);
    setDupLoaded(true);
    setBlocks(next);
    pastRef.current = []; futureRef.current = [];
    setHistVer((v) => v + 1);
    setDupNotices(notices || []);
  }, []);

  /**
   * PUT THE DOCUMENT BACK TO UNTOUCHED (2026-08-28, division feedback batch).
   *
   * Five separate reports — TPU ×2, KAN ×2, ATL — were one fault: the document
   * survives events that end it. `startOver` (FormView) reset three cosmetic
   * states and navigated to page 1, and `pickTemplate` cleared the flat preview
   * only. Everything below stayed, so the next request opened on the last one's
   * email and a newly-picked template was never assembled (`echTplName` is
   * `tplBespoke ? "" : tplName`, and StudioView's assembly effect returns on an
   * empty name).
   *
   * Measured, not assumed: nothing here is persisted — localStorage on the live
   * dev tab holds only `esBlockSnap.v1`, `dupJobs:<div>` and `echLists:v1`. That
   * is why a browser refresh fixed it, which is what TPU reported doing, and it
   * is why the fix belongs here rather than in a cache.
   *
   * blocks go back to STARTER, NOT to []. `docBuilt` is a divergence test against
   * STARTER, so an empty array reads as "the division built something" and would
   * ship a visual spec for a document nobody authored.
   *
   * NO visual change: same buttons, same labels, same layout (per Jack). The only
   * difference is that the fields come up blank the way they do after ⌘R.
   */
  const resetDocument = useCallback(() => {
    setTplHtml(""); setTplEdited(""); setTplBespoke(false);
    setDupLoaded(false); setDupNotices([]);
    setBlocks(STARTER.map(makeBlock));
    autoLoadedRef.current = "";
    pastRef.current = []; futureRef.current = [];
    setHistVer((v) => v + 1);
  }, []);

  /* STABLE IDENTITY (code review #13). As a plain function declaration this was a
     new reference on every render, and the Studio's ECH fill effect has it in its
     deps — so echFillAll walked the whole document on every keystroke, zoom change
     and selection. It converges (patches are diff-gated) so it was never a loop, but
     it is why a typed value had no window in which to survive. */
  /* ── EVERY EDIT IS UNDOABLE (2026-09-22, pilot #131) ─────────────────────────────────
     Kailey White (NEV) asked for a way to put a removed home back; Jack's ruling: no
     add-back strip — the ↶/↷ buttons ARE the answer, so they have to cover every edit,
     not just block moves. Field writes used to bypass history on purpose ("field edits
     stay live/non-history"), which left typing, picks, crops, button swaps and the row ✕
     all un-undoable. Now a field write records the document BEFORE it, COALESCED: the
     same block + the same keys inside 1.5 s is one typing session and one ⌘Z, so a
     word is not undone a letter at a time. Two rules keep this honest:
       · the FILL never enters history — `useEchDocFill` passes `{ history: false }`; a
         derived write is not the requester's edit, and recording it would bury theirs;
       · `blocksRef` is read at call time, so `patchData` keeps the stable identity the
         fill effect depends on (code review #13) and still snapshots the live document.
     Undo restores the pre-edit document; the fill then re-runs on it, which is exactly
     what "undo my typing" means for a fed field (the claim goes with the text). */
  const patchData = useCallback((id: string, patch: Record<string, unknown>, opts?: { history?: boolean }) => {
    if (!opts || opts.history !== false) {
      const key = id + "|" + Object.keys(patch).sort().join(",");
      const now = Date.now();
      const g = histGroupRef.current;
      const cur = blocksRef.current;
      if ((g.key !== key || now - g.at > 1500) && pastRef.current[pastRef.current.length - 1] !== cur) {
        pastRef.current.push(cur);
        if (pastRef.current.length > 80) pastRef.current.shift();
        futureRef.current = [];
        setHistVer((v) => v + 1);
      }
      histGroupRef.current = { key, at: now };
    }
    setBlocks((s) => s.map((b) => (b.id === id ? { ...b, data: { ...b.data, ...patch } } : b)));
  }, []);

  /* ── feedback engine (monolith L5462–5500) ── */
  /* downscale an image data-URL to ≤1400px wide (monolith esDownscale usage) */
  const downscale = (url: string, maxW: number, cb: (u: string) => void) => {
    try {
      const im = new Image();
      im.onload = () => {
        try {
          if (im.width <= maxW) { cb(url); return; }
          const c = document.createElement("canvas");
          const k = maxW / im.width;
          c.width = maxW; c.height = Math.round(im.height * k);
          const g = c.getContext("2d");
          if (!g) { cb(url); return; }
          g.drawImage(im, 0, 0, c.width, c.height);
          cb(c.toDataURL("image/jpeg", 0.85));
        } catch { cb(url); }
      };
      im.onerror = () => cb(url);
      im.src = url;
    } catch { cb(url); }
  };
  const fbAddFiles = (fl: FileList | File[] | null) => {
    Array.from(fl || []).forEach((f) => {
      if (!/^image\//.test(f.type)) return;
      setFbImgs((cur) => {
        if (cur.length >= 5) { toast("Up to 5 images."); return cur; }
        const r = new FileReader();
        r.onload = () => downscale(String(r.result), 1400, (u) =>
          setFbImgs((c2) => (c2.length >= 5 ? c2 : [...c2, { name: f.name || "screenshot-" + (c2.length + 1) + ".png", data: u }])));
        r.readAsDataURL(f);
        return cur;
      });
    });
  };
  /* auto-capture where the user was + their environment (monolith fbContext, 2026-07-27) */
  const fbContext = () => {
    const p: string[] = [];
    if (view === "studio") p.push("Where: Design Email", "Template: " + tplName);
    else {
      const steps: Record<number, string> = { 1: "Creative Request", 2: "Email Design", 3: "Audience", 4: disclaimerBuilderEnabled() ? "Disclaimer & Details" : "Branding & Details" };
      p.push("Step: " + (steps[ui.step] || "page " + ui.step));
    }
    if (div) p.push("Division: " + div);
    p.push("Source: Main");
    p.push("Screen: " + window.innerWidth + "×" + window.innerHeight);
    p.push("Browser: " + navigator.userAgent);
    p.push("URL: " + window.location.href);
    return p.join(" · ");
  };
  const fbSubmit = async () => {
    const msg = fbText.trim();
    if (msg.length < 5) { toast("Add a little more detail first."); return; }
    setFbBusy(true);
    try {
      const d = await sendFeedback({ message: msg, category: fbCat, division: div, by: fbBy.trim(), images: fbImgs, context: fbContext() });
      setFbBusy(false);
      if (d && d.ok) {
        setFbText(""); setFbImgs([]); setFbOpen(false);
        toast("Thanks — feedback sent to the team. ❤️");
      } else toast("Send failed: " + ((d && d.error) || "unknown"));
    } catch {
      setFbBusy(false);
      toast("Send failed — network.");
    }
  };
  const fbPickRef = useRef<HTMLInputElement>(null);

  /* ── Studio-scope AI copy (monolith esAIcopy → __aiCopyStudio() full scope) ──
     ctx mirrors __aiRead from app state (the form-scope instance adds dup/live-
     price context, which lives in FormView's refs). */
  const tplName = (Array.isArray(form.template) ? form.template[0] : form.template) || MVP_TPL_DEFAULT;
  /* the disclaimer Step 4 would build right now, drawn into Design Email's footer as the
     email changes (Jack, 2026-10-19). [] where the builder is off, so prod keeps the footer. */
  const studioLicense = useLicenseLine(form, disclaimerBuilderEnabled());
  const legalVer = useLegalDisclaimers(disclaimerBuilderEnabled());
  const studioDsParas = useMemo(() => (view === "studio" && disclaimerBuilderEnabled()
    ? liveDisclaimerParas({ doc: blocks as unknown as Array<{ type?: string; data?: Record<string, unknown> }>, template: tplName, reqFocus: form.reqFocus || "Market",
        form, answers: form.dsAnswers || {}, requestNo: disclaimerId(form.disclaimer || ""), emailHtml: studioHtml, license: studioLicense })
    : []), [view, blocks, tplName, form, studioHtml, studioLicense, legalVer]); // eslint-disable-line react-hooks/exhaustive-deps
  const studioAiCtx = (): CopyContext => {
    const divLabel = (divisionOptions().find((d) => d.code === form.divisionCode) || { label: "" }).label;
    return {
      city: (divLabel.split(/—|-/)[1] || "").trim(),
      community: (form.community || "").split(",")[0].trim() || undefined,
      masterplan: (form.masterplan || "").split(",")[0].trim() || undefined,
      aoi: form.areaOfInterest,
      initiative: form.initiative,
      message: messageHuman(form.primaryMessage || ""),
      off: messageIsOffer(form.primaryMessage || ""),
      template: tplName,
      templateStage: tplStageOf(tplName),
      project: form.projectName,
      focus: form.reqFocus,
      buyerProfile: form.utmTarget || seg.persona, /* the requester's pick first (A2, 2026-09-08) */
    };
  };
  /* monolith apply: __applyCopyToForm (subject/preheader) + __studioApplyCopy —
     every headline/eyebrow block, the FIRST body, cta labels by button order.
     Not history-recorded (the monolith calls esCanvas without esRecord). */
  const studioAiApply = (r: AiCopyResult) => {
    setForm({ ...form, subject: r.subject, preheader: r.preheader });
    setBlocks((s) => {
      let didBody = false, ctaN = 0;
      return s.map((b) => {
        if (b.type === "headline" && r.headline) return { ...b, data: { ...b.data, text: r.headline } };
        if (b.type === "eyebrow" && r.eyebrow) return { ...b, data: { ...b.data, text: r.eyebrow } };
        if (b.type === "body" && r.body && !didBody) { didBody = true; return { ...b, data: { ...b.data, text: r.body } }; }
        if (b.type === "cta") {
          ctaN++;
          const lab = ctaN === 1 ? r.cta1 : ctaN === 2 ? r.cta2 : null;
          if (lab) return { ...b, data: { ...b.data, label: lab } };
        }
        return b;
      });
    });
  };

  const user = getCurrentUser();

  /* ── SUBMITTED BY EMAIL, FROM THE SIGNED-IN ACCOUNT (2026-08-24) ──────────────
     `requestedByEmail` was initialised to "" at L103 and NOTHING in the codebase
     ever wrote it — grep returns exactly four hits: the type, this init, and the
     two places that READ it. So "Submitted by Email" left as "" on every submit,
     the worker drops empty strings before writing, and the column was blank on
     every intake row Katy has ever received (she reported it 2026-08-22). The
     app has had the value the whole time — the topbar renders `user.email`.
     Seeded once and only when empty, so it can never overwrite a real entry. */
  /* ── PICKING A TEMPLATE ON THE FORM REPLACES THE DOCUMENT (2026-08-28) ─────────
     Jack, on the local review build: "why is the email preview not sync to the
     selected template still?" Measured on his own path, in Chrome: with the picker
     reading **Coming Soon** the rail rendered the **Available Now** email — banner
     alt "Move-In Ready Homes Available Now", 41,087 bytes — and switching templates
     did not move it by a single byte.

     Nothing was wrong with the assembly. `echDoc` builds the right document for
     either name (the gallery cards, which assemble the same way, were correct
     throughout), and FormView's `previewHtml` followed the pick exactly. The fault
     is that the SWAP only ever existed inside StudioView's assembly effect, which
     runs while the Studio is MOUNTED. So once the Studio had produced a document,
     App's `blocks` stayed stamped with the template it was built from, and the
     rail's own order — `dupHtml || fillHtml || liveDocHtml || previewHtml` — put
     that stale document ahead of the freshly assembled one. Correct order (the
     document IS the email, 2026-08-24); the document just never changed.

     So the swap belongs where the document lives, in App, and not in either view:
     it has to happen whether the requester picks from the form's rail or from the
     Studio. Nothing is carried forward, exactly as StudioView's own comment says —
     a different template is a different email, and carrying body copy into it would
     be inventing an edit nobody made.

     THREE GUARDS:
      · a BESPOKE document owns itself — a duplicate, an "As sent · …" or a ★
        favorite is not an assembly of `tplName` and must never be overwritten by
        one (`tplBespoke` / `dupLoaded`, the same pair `echTplName` is gated on)
      · only a document that ALREADY carries ECH blocks is swapped. The untouched
        STARTER mock is left alone so a requester who has not opened the Studio sees
        exactly what they saw before, and `studioUsed` — a divergence test against
        that mock — keeps meaning what it means.
      · the stamp is the same composite StudioView writes (`echVersion() +
        echTplVersion()`), so its effect sees "same template, same markup" on the
        next mount and leaves the document completely alone. One assembly, not two.
     History is reset for the reason `loadTpl` resets it: the document was replaced
     wholesale, and the previous template must not be one ⌘Z away. */
  const echTplsApp = useEchTemplateList();
  useEffect(() => {
    if (tplBespoke || dupLoaded) return;
    const t = echTplFor(tplName);
    if (!t) return;
    const asm = echAsmOf(blocks);
    /* an ECH document from a DIFFERENT template — the only case to replace */
    if (!blocks.some((b) => isEch(b.type))) return;
    if (asm && asm.tpl === t) return;
    const doc = echDoc(t, makeBlock);
    if (!doc || !doc.length) return;
    echStamp(doc, t, echVersion() + echTplVersion());
    setBlocks(doc);
    pastRef.current = []; futureRef.current = [];
    setHistVer((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplName, echTplsApp.version, tplBespoke, dupLoaded]);

  useEffect(() => {
    const em = user?.email;
    if (!em) return;
    setForm((p) => (p.requestedByEmail ? p : { ...p, requestedByEmail: em }));
  }, [user?.email]);

  /* ── PICK UP WHERE YOU LEFT OFF (2026-09-08) — see services/draft.ts ────────────
     The in-progress request (Step-1/2 fields, audience, wizard page, Studio blocks)
     is written to the browser a moment after each change and OFFERED back on the
     next visit. Never restored silently; never saved while the offer is unanswered,
     so this blank mount cannot overwrite what is stored; cleared on a successful
     submit, on "Submit another" and on "Start fresh". */
  const draftWho = user?.email || form.requestedByEmail || "";
  const [resumeOffer, setResumeOffer] = useState<Draft | null | undefined>(undefined); // undefined = not looked yet
  useEffect(() => {
    if (resumeOffer !== undefined) return;
    setResumeOffer(draftLoad(draftWho));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const meaningful = draftMeaningful(form, docBuilt, tplEdited, formStep);
  useEffect(() => {
    if (resumeOffer === undefined) return;           // haven't looked yet
    if (resumeOffer && !meaningful) return;          // offer pending, nothing typed: keep the stored draft
    if (resumeOffer && meaningful) setResumeOffer(null); // they started something new — their work wins
    if (!meaningful) return;
    /* a proofing session is a submitted job, not the user's own request — never let it
       overwrite their saved in-progress draft */
    if (proofRoute) return;
    const t = window.setTimeout(() => {
      draftSave({ who: draftWho, form, seg, formStep, blocks, tplHtml, tplEdited, tplBespoke, dupLoaded });
    }, DRAFT_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, seg, formStep, blocks, tplHtml, tplEdited, tplBespoke, dupLoaded, meaningful, resumeOffer, draftWho]);
  /* put a stored request back on screen — shared by the browser draft (Resume) and a
     named draft opened from the template picker */
  const applyDraft = (d: NamedDraftDoc) => {
    setForm({ ...d.form, requestedByEmail: user?.email || d.form.requestedByEmail || "" });
    setSeg(d.seg);
    setFormStep(d.formStep || 1);
    setTplHtml(d.tplHtml || ""); setTplEdited(d.tplEdited || ""); setTplBespoke(!!d.tplBespoke);
    setDupLoaded(!!d.dupLoaded); setDupNotices([]);
    setBlocks(Array.isArray(d.blocks) && d.blocks.length ? d.blocks : STARTER.map(makeBlock));
    autoLoadedRef.current = "";
    pastRef.current = []; futureRef.current = [];
    setHistVer((v) => v + 1);
  };
  const resumeDraft = () => {
    const d = resumeOffer;
    if (!d) return;
    applyDraft(d);
    setResumeOffer(null);
    setView("form");
    toast("Picked up your draft — review before submitting.");
  };
  const discardDraft = () => { draftClear(draftWho); setResumeOffer(null); };

  /* ── NAMED DRAFTS + PREVIEW LINK (2026-09-18, pilot item 1; dev/QA only) ────────
     services/namedDrafts.ts has the why. App owns the document, so App composes the
     draft (the same shape the browser draft keeps) and hands the Studio a save
     function; the picker hands back a draft to open. `namedDraft` is the one this
     request was opened from or last saved as — "Copy preview link" re-saves under
     that name so the reviewer's link keeps pointing at the latest save. */
  /* ── PROOFING (Milestone 2, 2026-10-06) ─────────────────────────────────────────
     `?proof=<assetId>` (Review link) and `&mode=edit` (Editor link) open the job's latest
     saved version IN THE STUDIO — the Design Email screen people already use — with the
     comments on it (Jack's Oct 5 mockup). A reviewer gets Review Email: read-only, a click
     drops a pin. Digital Ops gets Edit Email: the usual block editing plus comments, rounds
     and "Save revision", which files vN+1 (never overwrites) and pushes the HTML to the
     job's Marketing Cloud asset on the existing revise path. Every rule is the server's;
     this only asks. */
  const proofRoute = useMemo(() => proofRouted(), []);
  const [proof, setProof] = useState<{ id: string; mode: "comment" | "edit"; data: ProofData; rerouted?: boolean } | null>(null);
  const [proofMsg, setProofMsg] = useState<{ title: string; body: string; reviewHref?: string } | null>(null);
  const [proofBusy, setProofBusy] = useState(false);
  const [proofSaving, setProofSaving] = useState(false);
  const [proofDirty, setProofDirty] = useState(false);
  /* what Digital Ops highlights for the division (10-26 call) — saved with the next revision */
  const [proofHl, setProofHl] = useState<ProofHighlight[]>([]);
  const [proofHlDirty, setProofHlDirty] = useState(false);
  useEffect(() => {
    if (!proofRoute) return;
    const id = proofRoute.id;
    if (!id) { setProofMsg({ title: "This link is missing the email number", body: "Open the job's link from Airtable again." }); return; }
    (async () => {
      const r = await getProof(id).catch(() => null);
      if (!r || !r.ok || !r.me || !r.state) {
        setProofMsg(r && r.status === 401
          ? { title: "Can't open this email", body: "We couldn't confirm who you are. Sign out, sign back in, then open the link from Airtable again." }
          : { title: "Can't open this email", body: (r && r.error) || "Couldn't reach the server. Check your connection and reload." });
        return;
      }
      /* Jack, 2026-10-06: anyone not on the editor list "will just get rerouted to commenter".
         A non-editor on the Editor link lands on the Review view of the same email — no
         refusal page — and the address bar drops &mode=edit so a refresh stays there. */
      const editAllowed = proofRoute.mode === "edit" && r.me.role !== "reviewer";
      if (proofRoute.mode === "edit" && !editAllowed) {
        try { window.history.replaceState(null, "", proofReviewUrl(id)); } catch { /* old browser */ }
      }
      const vs = r.state.versions;
      const latest = vs.length ? vs[vs.length - 1].n : 0;
      const raw = latest ? await getProofVersionDoc(id, latest).catch(() => "") : "";
      let doc: NamedDraftDoc | null = null;
      try { doc = raw ? (JSON.parse(raw) as NamedDraftDoc) : null; } catch { doc = null; }
      if (!doc || !doc.form || !Array.isArray(doc.blocks)) {
        setProofMsg({ title: "This email was submitted before comments moved into ECH", body: "Use the review link on the Airtable job for this one. New submissions open here." });
        return;
      }
      /* a version saved before a block was replaced in Content Builder points at the old
         asset id — re-point it by the block's name once the library is in hand
         (rebindEchTypes; the M2 Discovery demo opened blank without this, 2026-10-12) */
      try { echAdopt(await sfmcBlocksOnce(), "sfmc"); } catch { /* the snapshot, if any, still applies */ }
      const rb = rebindEchTypes(doc.blocks as never[]);
      if (rb.changed) doc = { ...doc, blocks: rb.blocks as typeof doc.blocks };
      applyDraft(doc);
      setProof({ id, mode: editAllowed ? "edit" : "comment", data: r, rerouted: proofRoute.mode === "edit" && !editAllowed });
      setProofDirty(false);
      /* an editor picks up the highlights of a revision that hasn't gone to the division yet;
         once a round has shown that version, the next round starts with none */
      {
        const lv = vs.length ? vs[vs.length - 1] : null;
        const sent = !!lv && (r.state.rounds || []).some((x) => x.base === lv.n);
        setProofHl(lv && !sent && lv.highlights ? lv.highlights : []);
        setProofHlDirty(false);
      }
      setView("studio");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const proofAct = async (op: ProofOp, body: Record<string, unknown>): Promise<string> => {
    if (!proof) return "";
    setProofBusy(true);
    const r = await proofPost(op, { id: proof.id, ...body }).catch(() => null);
    setProofBusy(false);
    if (!r || !r.ok) return (r && r.error) || "That didn't save. Try again.";
    setProof((p) => (p ? { ...p, data: r } : p));
    // opening a round sends this revision's highlights; the next round starts clean
    if (op === "unlock" && !body.relabel) { setProofHl([]); setProofHlDirty(false); }
    return "";
  };
  /* THE HTML OF WHAT'S ON THE CANVAS. `builtHtml` prefers `tplEdited`, which on an ECH
     template still holds the auto-loaded template preview — so a block edit never reached
     it (caught 2026-10-06 in the localhost drive: v2's document carried the edit, v2's HTML
     did not). An ECH document renders from its blocks, exactly as the Studio draws it. */
  const proofHtml = () => {
    const html = blocks.some((b) => isEch(b.type)) ? studioHtml : builtHtml;
    /* the footer as the editor sees it (2026-10-19): their typed text on the Edit Email link,
       else the built disclaimer — never Layne's placeholder, which is what studioHtml holds */
    const foot = blocks.filter((b) => isDisclaimerBlock(b.type)).pop();
    return footerWithDisclaimer(html, studioDsParas, foot ? String(((foot.data || {}) as Record<string, unknown>).__dsText || "") : "");
  };
  const saveProofVersion = async (): Promise<boolean> => {
    if (!proof || proofSaving) return false;
    const html = proofHtml();
    if (!html) { toast("Nothing to save yet."); return false; }
    setProofSaving(true);
    const doc: NamedDraftDoc = { form, seg, formStep, blocks, tplHtml, tplEdited, tplBespoke, dupLoaded };
    const r = await proofPost("save", { id: proof.id, html, doc: JSON.stringify(doc), highlights: proofHl }).catch(() => null);
    if (!r || !r.ok) { setProofSaving(false); toast((r && r.error) || "Couldn't save the revision."); return false; }
    setProof((p) => (p ? { ...p, data: r } : p));
    setProofDirty(false);
    setProofHlDirty(false);
    // Marketing Cloud gets the same HTML; a failure here is said out loud, the version stands
    const mc = await mirrorRevise({ assetId: proof.id, html, div: div || undefined }).catch(() => null);
    setProofSaving(false);
    /* no "v" numbers on screen (Katy, 10-26): a save is named by its round */
    toast(mc && mc.ok ? "Saved · Marketing Cloud updated" : "Saved, but Marketing Cloud didn't update. Save again to retry.");
    return true;
  };
  /* local `npm start` only: file what's on the canvas as a proofing job's version 1, so
     proofing can be tried on a real template without submitting anything. Compiled out
     of production builds. */
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    (window as unknown as { __echProofSeed?: (id: string) => Promise<unknown> }).__echProofSeed = (id: string) =>
      proofPost("save", { id, html: proofHtml(), doc: JSON.stringify({ form, seg, formStep, blocks, tplHtml, tplEdited, tplBespoke, dupLoaded }), first: true });
  });
  /* an editor's change marks the revision unsaved; the fill's own derived writes don't */
  const proofSet: typeof studioSet = (u) => { if (proof) setProofDirty(true); studioSet(u); };
  const proofPatch: typeof patchData = (id, p, opts) => { if (proof && !(opts && opts.history === false)) setProofDirty(true); patchData(id, p, opts); };

  const draftsOn = namedDraftsEnabled();
  const [namedDraft, setNamedDraft] = useState<{ id: string; name: string; previewUrl: string } | null>(null);
  const saveNamedDraft = async (name: string, html: string): Promise<{ ok: boolean; previewUrl?: string; error?: string }> => {
    const doc: NamedDraftDoc = { form, seg, formStep, blocks, tplHtml, tplEdited, tplBespoke, dupLoaded };
    type SaveR = { ok?: boolean; id?: string; previewUrl?: string; savedAt?: string; error?: string };
    const r: SaveR = await draftSaveRemote({ div, name, who: draftWho, html, doc: JSON.stringify(doc), tpl: tplBespoke ? "" : tplName }).catch((): SaveR => ({ ok: false, error: "network" }));
    if (!r.ok || !r.id) return { ok: false, error: r.error || "unknown" };
    setNamedDraft({ id: r.id, name, previewUrl: r.previewUrl || "" });
    return { ok: true, previewUrl: r.previewUrl || "" };
  };
  const openNamedDraft = async (d: NamedDraft) => {
    toast("Opening " + d.name + "…");
    const raw = await getNamedDraftDoc(d.id).catch(() => "");
    let doc: NamedDraftDoc | null = null;
    try { doc = raw ? (JSON.parse(raw) as NamedDraftDoc) : null; } catch { doc = null; }
    if (!doc || !doc.form || !Array.isArray(doc.blocks)) { toast("Couldn't open that draft."); return; }
    applyDraft(doc);
    setResumeOffer(null);
    setNamedDraft({ id: d.id, name: d.name, previewUrl: d.previewUrl || "" });
    setView("form");
    toast("Opened draft “" + d.name + "” — pick up where you left off.");
  };

  return (
    <div className="rb rb-app">
      {/* topbar (production parity) */}
      <header className="rb-topbar">
        {/* hosted Lennar wordmark (monolith topbar, L825) — 22px tall image, not text */}
        <img className="rb-logo" alt="Lennar" style={{ height: 22, width: "auto", display: "block" }}
          src="https://lennar.stensul.com/images/tenants/lennar/customer/modules/studio//6772dcf8eaa9a-1735580920.9612.png" />
        {/* the crumb swaps while the admin console is open (monolith adminToggle, L6327) */}
        <span className="rb-crumb">{view === "board" ? "Admin · Creative Review" : "Email Creative Hub"}</span>
        <span className="rb-spacer" />

        {/* ⚠️ DELIBERATE DIVERGENCE FROM THE MONOLITH (2026-08-19, Jack: "remove the
            admin button in the top right"). The monolith's topbar has a ⚙ Admin
            button that toggles the Creative Review console in place; it is gone from
            this header and must not be restored on a parity pass.
            The console ITSELF is untouched — `BoardView` still builds, still renders
            for `view === "board"`, and the crumb still swaps for it. It is reachable
            only by URL now (`?admin=1` / `#admin`, see adminRouted above). */}
        {view === "board" && (
          <button className="rb-topbtn" onClick={() => setView("form")}>← Back to form</button>
        )}
        {/* Signed-in identity FOLDED INTO Sign out (option 6, Jack 2026-08-21) —
            one object in the bar instead of a name plus a button. Renders nothing
            when auth is off or nobody is signed in.

            The identity still has to be readable at a glance: on a shared machine
            nothing else tells the next person they are inside someone else's
            session, and every job filed carries this account as the submitter. So
            the initials sit ON the button and the full name + email stay in the
            title, which keeps that check without spending a second slot on it. */}
        {user && (
          <button className="rb-topbtn rb-signout" onClick={signOut}
            title={`Signed in as ${user.name}${user.email ? ` (${user.email})` : ""} — sign out of Microsoft`}>
            <span className="rb-av" aria-hidden="true">{initials(user.name)}</span>
            Sign out
          </button>
        )}
        {/* Feedback sits LAST, right of Sign out (Jack 2026-08-21). Identity reads as
            a pair — name then the action that ends it — so putting Feedback between
            them split it; and Feedback is the one button here that is always present
            whether or not auth is on, so it anchors the end of the bar. */}
        <button className="rb-topbtn" title="Tell us what feels clunky, broken, or missing" onClick={() => setFbOpen(true)}>Feedback</button>
      </header>

      {/* ── body ── */}
      {/* ── THE DRAFT STRIP (2026-09-08, Jack picked look C of five) ──────────────────
          A slim line under the header rather than a banner that pushes the form down:
          a "Draft" chip first — the one word that answers "did I already submit this?"
          (a submitted request never appears here; submit clears the draft, and the chip
          makes that rule visible instead of implied) — then the facts that tell one
          draft from another: name, division, step, age. */}
      {view === "form" && resumeOffer && (
        <div className="rb-resume" role="status">
          <span className="rb-resume-chip">Draft</span>
          <div className="rb-resume-txt">
            <strong>Pick up where you left off?</strong>
            <b>{draftTitle(resumeOffer)}</b>
            {resumeOffer.form.divisionCode ? <><span className="rb-resume-sep">·</span>{resumeOffer.form.divisionCode}</> : null}
            <span className="rb-resume-sep">·</span>step {resumeOffer.formStep || 1} of 4
            <span className="rb-resume-sep">·</span>saved {draftAge(resumeOffer.savedAt)}
          </div>
          <button type="button" className="btn gold rb-resume-btn" onClick={resumeDraft}>Resume draft</button>
          <button type="button" className="rb-resume-link" onClick={discardDraft}>Start fresh</button>
        </div>
      )}
      {view === "form" && (
        <FormView form={form} setForm={setForm} seg={seg} setSeg={setSeg} step={formStep} setStep={setFormStep} builtHtml={builtHtml} blocks={blocks} setBlocks={studioSet} onPatch={patchData} makeBlock={makeBlock} studioHasDoc={!!tplHtml || dupLoaded} onOpenStudio={() => setView("studio")}
          onLoadStudioHtml={(name, html) => { autoLoadedRef.current = html; setTplHtml(html); setTplEdited(html); setTplBespoke(!!name && name !== tplName); setDupLoaded(false); }}
          onLoadStudioBlocks={loadStudioBlocks}
          onResetDocument={resetDocument}
          onOpenNamedDraft={draftsOn ? openNamedDraft : undefined}
          /* A NEW REQUEST KEEPS THE DIVISION (Jack 2026-08-28). A division files
             several in a row; making them re-pick it every time is why nobody
             would use the button. Everything else goes back to blank. */
          onResetForm={() => {
            /* THE KEPT DIVISION KEEPS ITS DEFAULT MARKET (2026-09-08, pre-UAT e2e A7).
               Market is a required Step-1 field that only onDivisionChange ever
               auto-chipped; a new request keeps the division, so that handler never
               ran again and every follow-on request started with an empty Market.
               Same rule as a fresh pick: the division's first market, from the
               snapshot (the live list only widens the OPTIONS). */
            setForm((f) => {
              const dm = f.divisionCode ? divMarkets(f.divisionCode) : [];
              return { projectName: "", divisionCode: f.divisionCode || "", requestedByEmail: f.requestedByEmail || "",
                ...(dm.length ? { market: [dm[0][0]] } : {}) };
            });
            setSeg(newSeg());
            draftClear(draftWho);
            setNamedDraft(null);
          }}
          onSubmitted={(filed) => {
            draftClear(draftWho);
            /* proofing version 1 = exactly what was submitted, with its editable document,
               so Digital Ops can comment on and edit it in ECH (M2, 2026-10-06). Best-effort:
               a job without v1 can still be commented on from its hosted preview. */
            if (filed && filed.assetId && filed.html) {
              const doc: NamedDraftDoc = { form, seg, formStep, blocks, tplHtml, tplEdited, tplBespoke, dupLoaded };
              proofPost("save", { id: filed.assetId, html: filed.html, doc: JSON.stringify(doc), first: true }).catch(() => undefined);
            }
            /* a named draft that just became a request: stamp it so the picker shows
               "Submitted" and the sent sweep can clear it (Jack: clears when SENT) */
            if (namedDraft) { const id = namedDraft.id; setNamedDraft(null); draftMarkSubmitted(id).catch(() => undefined); }
          }}
          studioUsed={docBuilt || pastRef.current.length > 0 || (!!tplEdited && tplEdited !== autoLoadedRef.current)} />
      )}

      {/* a proofing link that can't open the job: one plain card, nothing else of the form */}
      {proofRoute && !proof && (
        <div className="es on esproof pcomment">
          <div className="pmsg">
            <h2>{proofMsg ? proofMsg.title : "Opening the email…"}</h2>
            {proofMsg && <p>{proofMsg.body}</p>}
            {proofMsg && proofMsg.reviewHref && <div><a className="btn" href={proofMsg.reviewHref}>Open the review link</a></div>}
          </div>
        </div>
      )}

      {view === "studio" && (
        <StudioView
          blocks={blocks}
          setBlocks={proof ? proofSet : studioSet}
          onPatch={proof ? proofPatch : patchData}
          makeBlock={makeBlock}
          onOpenBynder={(apply, need, multi) => setBynderFor({ apply, need, multi })}
          onUndo={studioUndo}
          onRedo={studioRedo}
          canUndo={pastRef.current.length > 0}
          canRedo={futureRef.current.length > 0}
          onResetHistory={studioResetHist}
          tplHtml={tplHtml}
          tplEdited={tplEdited}
          setTplEdited={setTplEdited}
          /* what changed between the original send and now — shown once, above the
             canvas, and dismissed for good */
          dupNotices={dupNotices}
          onClearDupNotices={() => setDupNotices([])}
          onClose={() => setView("form")}
          aiCtx={studioAiCtx}
          onAiApply={studioAiApply}
          division={div}
          projectName={form.projectName}
          /* Step 1's scope drives every dynamic ECH block on the canvas (audit row
             19). Passed as fields, not as the whole FormState, so the Studio's
             dependency on the form stays exactly what it reads. */
          step1={{
            focus: form.reqFocus || "",
            divisionCode: form.divisionCode || "",
            community: form.community,
            masterplan: form.masterplan,
            areaOfInterest: form.areaOfInterest,
            market: form.market,
            initiative: form.initiative,
          }}
          disclaimerParas={studioDsParas}
          /* ── A RESEND GETS THE GUARDRAILS TOO (2026-08-31) ────────────────────
             Jack, on FNO pilot feedback: "Should we allow resends for templates we
             don't have locked down? There were no character limits and I could change
             icons." Both conditions matter: the request type says this is a resend,
             and `tplBespoke` says the canvas is the opaque template editor rather than
             assembled blocks (a standard template already has per-field panels with
             their own counters). The other bespoke paths — a division favorite, a
             community fill — are deliberately left as they were. */
          resend={/^Resend Existing Division Email/i.test(form.requestType || "") && tplBespoke}
          /* The template to ASSEMBLE from Layne's SFMC blocks (audit row 23).
             Empty whenever the loaded document is bespoke — see tplBespoke. */
          echTplName={tplBespoke ? "" : tplName}
          /* the Milestone 2 panel's template selector writes the same form field Step 1 does */
          onPickTemplate={(n) => { setTplBespoke(false); setForm((f) => ({ ...f, template: Array.isArray(f.template) ? [n] : n })); }}
          namedDrafts={draftsOn && !proof ? { current: namedDraft ? { name: namedDraft.name, previewUrl: namedDraft.previewUrl } : null, save: saveNamedDraft } : undefined}
          /* the ONLY place the disclaimer can be typed over (Jack, 2026-10-19) */
          disclaimerEditable={!!proof && proof.mode === "edit" && proof.data.can?.editContent !== false}
          proof={proof ? {
            id: proof.id, mode: proof.mode, data: proof.data, rerouted: proof.rerouted, busy: proofBusy, saving: proofSaving, dirty: proofDirty || proofHlDirty,
            act: proofAct, onSaveRevision: saveProofVersion,
            hl: proofHl, setHl: (h: ProofHighlight[]) => { setProofHl(h); setProofHlDirty(true); },
            reviewLink: window.location.origin + window.location.pathname + proofReviewUrl(proof.id).replace(/&as=[^&]*/, ""),
            editorLink: window.location.origin + window.location.pathname + proofReviewUrl(proof.id, true).replace(/&as=[^&]*/, ""),
          } : undefined}
        />
      )}

      {/* the admin console (monolith #admin) — the ⚙ toggle swaps it in place;
          the previous "Template Edit" route had no monolith counterpart (removed) */}
      {view === "board" && <div className="rb-viewwrap"><BoardView division={div} /></div>}

      {bynderFor && (
        <BynderPicker divisionCode={div} needW={bynderFor.need?.w} needH={bynderFor.need?.h} onClose={() => setBynderFor(null)} onPick={(url, asset) => bynderFor.apply(url, asset)} multi={bynderFor.multi ? { ...bynderFor.multi, onDone: (urls, assets) => bynderFor.multi!.onDone(urls, assets) } : undefined} />
      )}

      {/* feedback modal — monolith #fbModal (L1673): category cards + name +
          screenshots (click/drag/paste) + auto-captured context */}
      {fbOpen && (
        <div className="rb-ovl" onClick={() => setFbOpen(false)}>
          <div className="rb-modal" style={{ width: "min(520px, 96vw)", maxHeight: "88vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}
            onPaste={(e) => { const items = e.clipboardData?.files; if (items && items.length) { e.preventDefault(); fbAddFiles(items); } }}>
            <div className="rb-modal-hd"><b>Feedback</b><button onClick={() => setFbOpen(false)}>✕</button></div>
            <div style={{ padding: "18px 20px" }}>
              <div className="fb-lab">What kind of feedback?</div>
              <div className="fb-cats">
                {([["User Experience", "💫 User experience", "what's clunky"], ["Technical Issue", "🔧 Technical issue", "something broke"], ["Wish List", "✨ Wish list", "future ideas"]] as [string, string, string][]).map(([v, l, s]) => (
                  <button key={v} type="button" className={fbCat === v ? "on" : ""} onClick={() => setFbCat(v)}>{l}<small>{s}</small></button>
                ))}
              </div>
              <div className="fb-lab" style={{ margin: "16px 0 8px" }}>Tell us more</div>
              <textarea value={fbText} placeholder="What happened, what felt off, or what would make this better…" onChange={(e) => setFbText(e.target.value)}
                style={{ width: "100%", minHeight: 110, border: "1px solid #d3d9e2", borderRadius: 10, padding: "11px 13px", font: "inherit", fontSize: 13, resize: "vertical", boxSizing: "border-box" }} />
              <input value={fbBy} placeholder="Your name (optional)" onChange={(e) => setFbBy(e.target.value)}
                style={{ width: "100%", marginTop: 10, border: "1px solid #d3d9e2", borderRadius: 10, padding: "10px 13px", font: "inherit", fontSize: 12.5, boxSizing: "border-box" }} />
              <div className="fb-lab" style={{ margin: "16px 0 8px" }}>Attach screenshots <span style={{ fontWeight: 600, textTransform: "none", letterSpacing: 0, color: "#94a3b8" }}>(optional)</span></div>
              <button type="button" onClick={() => fbPickRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); fbAddFiles(e.dataTransfer.files); }}
                style={{ width: "100%", border: "1.5px dashed #cbd5e1", borderRadius: 10, padding: 14, textAlign: "center", color: "#64748b", fontSize: 12.5, cursor: "pointer", background: "#f8fafc", fontFamily: "inherit" }}>
                📎 Click, drag, or paste images here
              </button>
              <input ref={fbPickRef} type="file" accept="image/*" multiple style={{ display: "none" }}
                onChange={(e) => { fbAddFiles(e.target.files); e.target.value = ""; }} />
              {fbImgs.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 9 }}>
                  {fbImgs.map((im, i) => (
                    <div key={i} style={{ position: "relative", width: 66, height: 50, borderRadius: 6, overflow: "hidden", border: "1px solid #e2e8f0", backgroundColor: "#f1f5f9", backgroundImage: `url('${im.data}')`, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" }}>
                      <button type="button" title="Remove" onClick={() => setFbImgs((c) => c.filter((_, j) => j !== i))}
                        style={{ position: "absolute", top: 1, right: 1, border: 0, background: "rgba(15,23,42,.65)", color: "#fff", width: 16, height: 16, borderRadius: 8, fontSize: 10, lineHeight: 1, cursor: "pointer", padding: 0 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" disabled={fbBusy} onClick={fbSubmit}
                style={{ width: "100%", marginTop: 14, border: 0, borderRadius: 10, background: "#1a2654", color: "#fff", font: "800 13px Inter,sans-serif", padding: 12, cursor: "pointer", opacity: fbBusy ? .7 : 1 }}>
                {fbBusy ? "Sending…" : "Send feedback"}
              </button>
              <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 9, textAlign: "center" }}>Goes straight to the team's review queue.</div>
            </div>
          </div>
        </div>
      )}

      {/* bottom-center toast (monolith __fvToast) */}
      <div className={"rb-toast" + (toastOn ? " on" : "")}>{toastMsg}</div>
    </div>
  );
}
